import { PaymentUtils } from '@ravago/shared/next-data-access/client';
import { InvoiceForCurrencyForLegalEntity } from '@ravago/shared/page-data/models/elements';

import { useMutation } from '@tanstack/react-query';

import { parseAsString, parseAsStringLiteral, useQueryState, useQueryStates } from 'nuqs';

import { STRIPE_QUERY_PARAMS } from '../constants';
import { PaymentOptions } from '../models/payment-options.model';
import { getInvoicesForLegalEntityAndCurrency, getInvoicesForPaymentType } from '../utils/invoice.utils';

export const usePaymentOptions = (openInvoices: InvoiceForCurrencyForLegalEntity[], selectedInvoiceIds: number[]) => {
   const [paymentOptionsQueryStates, setPaymentOptions] = useQueryStates({
      paymentType: parseAsStringLiteral(['selected', 'open', 'overdue'] as const),
      legalEntity: parseAsString,
      currency: parseAsString
   });
   const [paymentProviderId, setPaymentProviderId] = useQueryState(STRIPE_QUERY_PARAMS.PAYMENT_INTENT_CLIENT_SECRET);

   const createPaymentIntentMutation = useMutation({
      mutationKey: ['payment-intent'],
      mutationFn: (invoiceIds: number[]) => PaymentUtils.createPaymentIntent(invoiceIds),
      onSuccess: async (data) => {
         await setPaymentProviderId(data.paymentProviderId);
      }
   });

   const paymentOptions: PaymentOptions | null =
      paymentOptionsQueryStates.paymentType && paymentOptionsQueryStates.legalEntity && paymentOptionsQueryStates.currency
         ? (paymentOptionsQueryStates as PaymentOptions)
         : null;

   const startPayment = async (options: PaymentOptions) => {
      await setPaymentOptions(options);

      const transactions = getInvoicesForLegalEntityAndCurrency(openInvoices, options.legalEntity, options.currency);
      if (!transactions) return;
      const invoiceIds = getInvoicesForPaymentType(transactions, selectedInvoiceIds, options.paymentType).map(({ id }) => id);
      createPaymentIntentMutation.mutate(invoiceIds);
   };

   const clearPayment = async () => {
      createPaymentIntentMutation.reset();
      await setPaymentOptions(null);
      await setPaymentProviderId(null);
   };

   return {
      paymentOptions,
      paymentProviderId,
      paymentProviderLoading: createPaymentIntentMutation.isPending,
      paymentProviderError: createPaymentIntentMutation.isError,
      startPayment,
      clearPayment
   };
};
